/**
 * Ranking de Clientes — Fase C
 * Tabla ordenable de talleres con plan pago:
 *   · Total facturado (USD) — default
 *   · Antigüedad
 *   · Mensualidades pagadas
 *   · % Puntualidad
 * Top N (10/25/50/100), export CSV.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Trophy, Download, RefreshCw, ArrowUpDown, Loader2, Send, Medal, Calendar, DollarSign, Percent, ChevronUp, ChevronDown,
} from 'lucide-react';
import { rankingClientesAPI } from '../../services/api';

const NIVEL_LABEL = {
  estandar: 'Standard',
  estandar_pro: 'Standard Pro',
  premium: 'Premium',
};

const NIVEL_BADGE = {
  premium: 'bg-amber-100 text-amber-800 border-amber-300',
  estandar_pro: 'bg-blue-100 text-blue-800 border-blue-300',
  estandar: 'bg-slate-100 text-slate-700 border-slate-300',
};

const formatUSD = (n) => {
  if (n == null || isNaN(n)) return '—';
  return `US$ ${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const podioIcon = (pos) => {
  if (pos === 1) return <Medal className="h-5 w-5 text-amber-500 fill-amber-200" />;
  if (pos === 2) return <Medal className="h-5 w-5 text-slate-400 fill-slate-200" />;
  if (pos === 3) return <Medal className="h-5 w-5 text-orange-600 fill-orange-200" />;
  return <span className="text-gray-400 font-mono text-xs w-5 text-center">#{pos}</span>;
};

const RankingClientes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState(null);

  // Filtros
  const [sortBy, setSortBy] = useState('total_facturado');
  const [order, setOrder] = useState('desc');
  const [top, setTop] = useState(25);
  const [mesesVentana, setMesesVentana] = useState(12);
  const [incluirExtras, setIncluirExtras] = useState(true);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await rankingClientesAPI.getRanking({
        sort_by: sortBy,
        order,
        top,
        meses_ventana: mesesVentana,
        incluir_extras: incluirExtras,
      });
      setData(r.data);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar el ranking');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await rankingClientesAPI.getRanking({
          sort_by: sortBy,
          order,
          top,
          meses_ventana: mesesVentana,
          incluir_extras: incluirExtras,
        });
        if (active) setData(r.data);
      } catch (e) {
        if (active) toast.error('Error al cargar el ranking');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [sortBy, order, top, mesesVentana, incluirExtras]);

  const handleHeaderSort = (col) => {
    if (sortBy === col) {
      setOrder(order === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setOrder('desc');
    }
  };

  const sortIndicator = (col) => {
    if (sortBy !== col) return <ArrowUpDown className="h-3 w-3 inline-block ml-1 opacity-40" />;
    return order === 'desc'
      ? <ChevronDown className="h-3 w-3 inline-block ml-1 text-teal-600" />
      : <ChevronUp className="h-3 w-3 inline-block ml-1 text-teal-600" />;
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const r = await rankingClientesAPI.exportCSV({
        sort_by: sortBy,
        order,
        top: 500,
        meses_ventana: mesesVentana,
        incluir_extras: incluirExtras,
      });
      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ranking_clientes_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV descargado');
    } catch {
      toast.error('Error al exportar CSV');
    } finally {
      setExporting(false);
    }
  };

  const enviarMensaje = (taller) => {
    const nombre = encodeURIComponent(taller?.razon_social || '');
    navigate(`/admin/suscripciones?activeTab=notificaciones&taller_ids=${taller.taller_id}&taller_nombre=${nombre}`);
  };

  const totalGlobal = useMemo(() => {
    if (!data?.ranking) return 0;
    return data.ranking.reduce((acc, r) => acc + (r.total_facturado_usd || 0), 0);
  }, [data]);

  return (
    <div className="space-y-4" data-testid="ranking-clientes-tab">
      {/* Header + Filtros */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-500" />
                Ranking de Clientes
              </CardTitle>
              <CardDescription>
                Solo talleres con plan pago (Standard, Standard Pro, Premium). Montos ARS se convierten a USD con la cotización del día.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={cargar} disabled={loading} data-testid="ranking-refresh-btn">
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refrescar
              </Button>
              <Button onClick={handleExportCSV} disabled={exporting || !data?.ranking?.length} size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="ranking-export-csv-btn">
                {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Exportar CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Ordenar por</label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="ranking-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="total_facturado">Total facturado</SelectItem>
                  <SelectItem value="antiguedad">Antigüedad</SelectItem>
                  <SelectItem value="mensualidades">Mensualidades</SelectItem>
                  <SelectItem value="puntualidad">% Puntualidad</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Orden</label>
              <Select value={order} onValueChange={setOrder}>
                <SelectTrigger data-testid="ranking-order-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Mayor a menor</SelectItem>
                  <SelectItem value="asc">Menor a mayor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Top</label>
              <Select value={top.toString()} onValueChange={(v) => setTop(parseInt(v))}>
                <SelectTrigger data-testid="ranking-top-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">Top 10</SelectItem>
                  <SelectItem value="25">Top 25</SelectItem>
                  <SelectItem value="50">Top 50</SelectItem>
                  <SelectItem value="100">Top 100</SelectItem>
                  <SelectItem value="500">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Ventana facturado</label>
              <Select value={mesesVentana.toString()} onValueChange={(v) => setMesesVentana(parseInt(v))}>
                <SelectTrigger data-testid="ranking-ventana-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">Últimos 3 meses</SelectItem>
                  <SelectItem value="6">Últimos 6 meses</SelectItem>
                  <SelectItem value="12">Último año</SelectItem>
                  <SelectItem value="24">Últimos 2 años</SelectItem>
                  <SelectItem value="120">Todo el historial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              id="ranking-extras"
              type="checkbox"
              checked={incluirExtras}
              onChange={(e) => setIncluirExtras(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
              data-testid="ranking-extras-checkbox"
            />
            <label htmlFor="ranking-extras" className="text-sm text-gray-700 cursor-pointer">
              Incluir extras (usuarios extra, capacitaciones, gestoría, documentos sueltos) en el "Total facturado"
            </label>
          </div>
        </CardContent>
      </Card>

      {/* KPIs resumen */}
      {data?.meta && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Clientes en ranking</p>
                  <p className="text-3xl font-bold">{data.meta.total_clientes}</p>
                </div>
                <Trophy className="h-10 w-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500 to-green-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Total facturado (visible)</p>
                  <p className="text-2xl font-bold">{formatUSD(totalGlobal)}</p>
                </div>
                <DollarSign className="h-10 w-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Cotización USD/ARS</p>
                  <p className="text-2xl font-bold">
                    {data.meta.cotizacion_usd_ars
                      ? `$${Number(data.meta.cotizacion_usd_ars).toLocaleString('es-AR')}`
                      : 'N/D'}
                  </p>
                </div>
                <Calendar className="h-10 w-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabla */}
      <Card>
        <CardContent className="pt-6">
          {loading && !data ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
            </div>
          ) : !data?.ranking?.length ? (
            <p className="text-center text-gray-500 py-8 italic">No hay clientes para mostrar con estos filtros.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ranking-tabla">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 px-2 w-10">#</th>
                    <th className="py-2 px-2">Razón Social</th>
                    <th className="py-2 px-2">Nivel</th>
                    <th
                      className="py-2 px-2 cursor-pointer hover:bg-gray-50 text-right"
                      onClick={() => handleHeaderSort('total_facturado')}
                      data-testid="ranking-th-facturado"
                    >
                      <DollarSign className="h-3 w-3 inline-block mr-1" />
                      Total facturado
                      {sortIndicator('total_facturado')}
                    </th>
                    <th
                      className="py-2 px-2 cursor-pointer hover:bg-gray-50 text-center"
                      onClick={() => handleHeaderSort('antiguedad')}
                      data-testid="ranking-th-antiguedad"
                    >
                      <Calendar className="h-3 w-3 inline-block mr-1" />
                      Antigüedad
                      {sortIndicator('antiguedad')}
                    </th>
                    <th
                      className="py-2 px-2 cursor-pointer hover:bg-gray-50 text-center"
                      onClick={() => handleHeaderSort('mensualidades')}
                      data-testid="ranking-th-mensualidades"
                    >
                      Mensualidades
                      {sortIndicator('mensualidades')}
                    </th>
                    <th
                      className="py-2 px-2 cursor-pointer hover:bg-gray-50 text-center"
                      onClick={() => handleHeaderSort('puntualidad')}
                      data-testid="ranking-th-puntualidad"
                    >
                      <Percent className="h-3 w-3 inline-block mr-1" />
                      Puntualidad
                      {sortIndicator('puntualidad')}
                    </th>
                    <th className="py-2 px-2 text-right w-20">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ranking.map((r, idx) => (
                    <tr key={r.taller_id} className="border-b hover:bg-gray-50" data-testid={`ranking-row-${r.taller_id}`}>
                      <td className="py-2 px-2">
                        <div className="flex items-center justify-center">
                          {podioIcon(idx + 1)}
                        </div>
                      </td>
                      <td className="py-2 px-2">
                        <p className="font-medium text-gray-900">{r.razon_social}</p>
                        <p className="text-xs text-gray-500 font-mono">{r.numero_anac || '—'} · {r.pais || '—'}{r.provincia ? `, ${r.provincia}` : ''}</p>
                      </td>
                      <td className="py-2 px-2">
                        <Badge variant="outline" className={NIVEL_BADGE[r.nivel_suscripcion] || ''}>
                          {NIVEL_LABEL[r.nivel_suscripcion] || r.nivel_suscripcion}
                        </Badge>
                        {r.debito_automatico && (
                          <Badge className="ml-1 bg-emerald-100 text-emerald-700 text-xs">DA</Badge>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right font-mono font-semibold text-emerald-700">
                        {formatUSD(r.total_facturado_usd)}
                      </td>
                      <td className="py-2 px-2 text-center text-gray-700">{r.antiguedad_label || '—'}</td>
                      <td className="py-2 px-2 text-center">{r.mensualidades_pagadas}</td>
                      <td className="py-2 px-2 text-center">
                        {r.puntualidad_pct == null ? (
                          <span className="text-gray-400 text-xs">—</span>
                        ) : (
                          <span className={`font-semibold ${
                            r.puntualidad_pct >= 90 ? 'text-emerald-600' :
                            r.puntualidad_pct >= 70 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {r.puntualidad_pct}%
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => enviarMensaje(r)}
                          title="Enviar mensaje directo a este taller"
                          className="h-8 w-8 text-teal-600 hover:text-teal-700"
                          data-testid={`ranking-enviar-${r.taller_id}`}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RankingClientes;
